/**
 * ชนิดข้อมูลของ Delivery/Dispatch (P2-9g, D-105) — เขียนตาม Pydantic schema จริงที่
 * เซิร์ฟเวอร์ serve (`apps/api/src/cap_erp_api/modules/sales/schemas.py`,
 * `.../delivery.py`, `.../packing.py`) ตรวจกับ router จริง
 * (`apps/api/src/cap_erp_api/modules/sales/router.py`) ก่อนเขียนไฟล์นี้แล้วพบว่า
 * ห้าเส้นที่ WO นี้ต้องใช้ตรงกับ `docs/API_CONTRACT.md` หัวข้อ "Packing & Delivery"
 * ทุกเส้น (`POST /v1/packing-units/{id}/load`, `POST /v1/deliveries`,
 * `GET /v1/deliveries`, `GET /v1/deliveries/{id}`,
 * `POST /v1/deliveries/{id}/reverse`) — **ไม่มี discrepancy แบบที่
 * `WO-P2-M9-006` เจอกับ `POST /v1/packing-units/{id}/lots`**
 *
 * **ฝาแฝดของ `lib/packing/types.ts` ไม่ใช่ผู้ใช้ของมัน** — `lib/packing/**` อยู่ใน
 * forbidden_scope ของ Work Order นี้ (invariant "COPY PackingScreen's SHAPE, NOT
 * ITS FILES") ค่าคงที่ที่ควรจะเท่ากัน (เช่น รหัสโมดูล, ความยาวรหัสโรงงาน) จึงถูก
 * ประกาศซ้ำที่นี่โดยตั้งใจ ผูกกันด้วยค่าที่บังเอิญเท่ากัน ไม่ใช่ import เดียวกัน —
 * เหตุผลเดียวกับที่ `lib/packing/types.ts` ไม่ import จาก `lib/sales/types.ts`
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — เดียวกับใบสั่งขายและ Packing (`SALES_MODULE`
 * ใน `modules/sales/schemas.py`) เพราะ Delivery เป็นครึ่งหลังของงานส่งของแผนกขาย */
export const DELIVERY_MODULE = 'SALES';

/* ------------------------------------------------------------- สถานะ ----- */

export const DRAFT_PACKING_STATUS = 'DRAFT';
export const PACKING_PACKING_STATUS = 'PACKING';
export const PACKED_PACKING_STATUS = 'PACKED';
export const LOADED_PACKING_STATUS = 'LOADED';
export const DISPATCHED_PACKING_STATUS = 'DISPATCHED';
export const RETURNED_PACKING_STATUS = 'RETURNED';

/**
 * หกสถานะของ `packing_unit.status` (`modules/sales/packing.py`,
 * `PackingUnitStatus`) — หน้าจอนี้ (`WO-P2-M9-007`) เขียนได้สองทางบนกล่อง:
 * `load` สลับ `PACKED` → `LOADED` และ `POST /v1/deliveries` สลับ `LOADED` →
 * `DISPATCHED` ผ่านการสร้าง delivery — ไม่ใช่ transition ตรงบนกล่อง แต่ผลคือ
 * กล่องขยับสถานะเหมือนกัน `RETURNED` เป็นผลของ `reverse` บนฝั่ง delivery
 */
export const PACKING_UNIT_STATUSES = [
  DRAFT_PACKING_STATUS,
  PACKING_PACKING_STATUS,
  PACKED_PACKING_STATUS,
  LOADED_PACKING_STATUS,
  DISPATCHED_PACKING_STATUS,
  RETURNED_PACKING_STATUS,
] as const;

export type PackingUnitStatus = (typeof PACKING_UNIT_STATUSES)[number];

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */

/** `plant_code` ≤10 (`master_data/models.py PLANT_CODE_LENGTH`) */
export const PLANT_CODE_MAX_LENGTH = 10;
/** `so_number` ≤20 (`sales/models.py SO_NUMBER_LENGTH`) */
export const SO_NUMBER_MAX_LENGTH = 20;
/** `customer_code` ≤20 (`schemas.py CUSTOMER_CODE_LENGTH`) */
export const CUSTOMER_CODE_MAX_LENGTH = 20;
/** `reversal_reason` ≤255 (`delivery.py REVERSAL_REASON_LENGTH`) — เดียวกับ
 * `ck_delivery_reversal_record_is_whole` ที่บังคับให้ต้องมีเมื่อกลับรายการ */
export const REVERSAL_REASON_MAX_LENGTH = 255;

/* ------------------------------------------------------------- กฎธุรกิจ --- */
/* รหัสกฎที่ `service.py` ตั้งชื่อไว้จริง — เฉพาะที่ `load`/`dispatch`/`reverse`
 * ตอบได้เท่านั้น เรียงตามลำดับที่แต่ละฟังก์ชันตรวจจริง (module docstring ของ
 * `dispatch_package`/`reverse_delivery` ใน `service.py`) */

/** `403` — โรงงานที่ระบุอยู่นอก `plant_scope` ของผู้เรียก (D-004) เหมือนทุกโดเมน —
 * `load`/`dispatch`/`reverse` ทั้งสามเป็นการเขียนเข้าโรงงาน จึงตอบ 403 ไม่ใช่ 404 */
export const PLANT_OUTSIDE_SCOPE = 'PLANT_OUTSIDE_SCOPE';
/** `404` — ไม่มีกล่องเลขนี้ (`dispatch` ค้นด้วย `package_no` ที่สแกน) */
export const PACKING_UNIT_NOT_FOUND = 'PACKING_UNIT_NOT_FOUND';
/** `409` — กล่องอยู่ในสถานะที่ `load`/`dispatch` ไม่ได้ (`details.current_status`,
 * `details.target_status`, `details.allowed_source_statuses`) — ใช้ร่วมกันทั้งสอง
 * transition เหมือนที่ `service.py` ใช้รหัสเดียวกันสำหรับทั้งคู่ */
export const PACKING_UNIT_TRANSITION_NOT_ALLOWED = 'PACKING_UNIT_TRANSITION_NOT_ALLOWED';
/** `409` — ส่ง `expected_status` มาแล้วไม่ตรงกับที่เก็บจริง (หน้าจอนี้ไม่ส่งฟิลด์นี้) */
export const PACKING_UNIT_STATUS_CHANGED = 'PACKING_UNIT_STATUS_CHANGED';
/** `422` — ใบสั่งขายถูกยกเลิกไปแล้วระหว่างที่กล่องรอส่ง */
export const SALES_ORDER_NOT_DELIVERABLE = 'SALES_ORDER_NOT_DELIVERABLE';
/** `422` — lot ในกล่องไม่ได้อยู่ในสถานะ RELEASED อีกแล้ว (ตรวจซ้ำที่ประตูโรงงาน) */
export const LOT_NOT_RELEASED = 'LOT_NOT_RELEASED';
/** `422` — การส่งครั้งนี้จะทำให้จำนวนที่ส่งรวมเกินจำนวนที่ลูกค้าสั่ง (ไม่มี tolerance) */
export const SHIPPING_MORE_THAN_ORDERED = 'SHIPPING_MORE_THAN_ORDERED';
/** `422` — ยังไม่ได้ตั้งค่าบัญชีสำหรับการส่งของประเภทนี้ในบริษัทนี้ */
export const NO_ACCOUNT_MAPPING = 'NO_ACCOUNT_MAPPING';
/** `422` — สินค้านี้ไม่มีต้นทุนต่อหน่วยในโรงงานนี้ ตีมูลค่าไม่ได้ */
export const NO_UNIT_COST = 'NO_UNIT_COST';
/** `422` — การส่งนี้ถูกกลับรายการไปแล้ว — **ไม่ replay** ต่างจากการสแกนซ้ำตอนส่ง
 * (`details.delivery_no`, `details.reversed_at`, `details.reversed_by_employee_id`,
 * `details.reversal_reason`) */
export const DELIVERY_ALREADY_REVERSED = 'DELIVERY_ALREADY_REVERSED';
/** `404` — ไม่มี delivery รหัสนี้ (หรืออยู่นอกขอบเขตโรงงาน — สองกรณีตอบเหมือนกัน) */
export const DELIVERY_NOT_FOUND = 'DELIVERY_NOT_FOUND';
/** `400` — ฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของถูกส่งมาในคำขอ (เลข delivery, จำนวนที่ส่งจริง,
 * ผู้กระทำ ฯลฯ) */
export const DELIVERY_FIELD_IS_SERVER_OWNED = 'DELIVERY_FIELD_IS_SERVER_OWNED';
/** `400` — ฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของถูกส่งมาในคำขอของ `load` (ใช้ guard เดียวกับ
 * Packing — `reject_server_owned_packing_fields`) */
export const PACKING_UNIT_FIELD_IS_SERVER_OWNED = 'PACKING_UNIT_FIELD_IS_SERVER_OWNED';

/* ------------------------------------------------------------- resource --- */

/**
 * กล่องหนึ่งใบ **ไม่มี lot** — รูปของแต่ละ item ใน `GET /v1/packing-units`
 * (`PackingUnitListItemResponse`) ทุกฟิลด์ตรงตาม schema เซิร์ฟเวอร์ตรง ๆ — สี่ฟิลด์
 * "pack record"/"unpack record" เป็น `string | null` ที่เหลือ **required เสมอ**
 */
export interface PackingUnitSummary {
  readonly packing_unit_id: string;
  readonly package_no: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly so_id: string;
  readonly so_number: string;
  readonly line_no: number;
  readonly product_id: string;
  readonly product_code: string;
  readonly packing_standard_id: string;
  readonly storage_location_id: string;
  readonly storage_location_code: string;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ — ห้ามแปลงเป็น number */
  readonly packed_qty: string;
  readonly uom_code: string;
  readonly status: string;
  readonly created_by_employee_id: string;
  readonly packed_by_employee_id: string | null;
  readonly packed_at: string | null;
  readonly unpacked_by_employee_id: string | null;
  readonly unpacked_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/packing-units` — envelope มาตรฐานของทุกกลุ่ม */
export interface PackingUnitListPage {
  readonly items: readonly PackingUnitSummary[];
  readonly next_cursor: string | null;
}

/** หนึ่ง lot ที่ออกไปในเดลิเวอรีนี้ — `DeliveryLotResponse` มีแค่สองฟิลด์ */
export interface DeliveryLot {
  readonly lot_id: string;
  readonly shipped_qty: string;
}

/**
 * หนึ่งเดลิเวอรี **ไม่มี lot** — รูปของแต่ละ item ใน `GET /v1/deliveries`
 * (`DeliveryListItemResponse`) ทุกฟิลด์ตรงตาม schema เซิร์ฟเวอร์ — สามฟิลด์การกลับ
 * รายการ (`reversed_at`/`reversed_by_employee_id`/`reversal_reason`) เป็น
 * `string | null` พร้อมกันเสมอ (`ck_delivery_reversal_record_is_whole`) ที่เหลือ
 * required
 */
export interface DeliverySummary {
  readonly delivery_id: string;
  readonly delivery_no: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly packing_unit_id: string;
  readonly package_no: string;
  readonly so_id: string;
  readonly so_number: string;
  readonly line_no: number;
  readonly customer_id: string;
  readonly customer_code: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly storage_location_id: string;
  readonly storage_location_code: string;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ — สิ่งที่ออกจากโรงงานจริงวันนั้น ไม่ขยับแม้จะ
   * ถูกกลับรายการ (D-105 ข้อ 6) */
  readonly shipped_qty: string;
  readonly uom_code: string;
  readonly packing_unit_status: string;
  readonly dispatched_by_employee_id: string;
  readonly dispatched_at: string;
  readonly reversed_at: string | null;
  readonly reversed_by_employee_id: string | null;
  readonly reversal_reason: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** เดลิเวอรีหนึ่งใบ **พร้อม lot ครบ** — รูปของ `GET /{id}` (`DeliveryResponse`) */
export interface Delivery extends DeliverySummary {
  readonly lots: readonly DeliveryLot[];
}

/** `GET /v1/deliveries` — envelope มาตรฐานของทุกกลุ่ม */
export interface DeliveryListPage {
  readonly items: readonly DeliverySummary[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests -- */

/**
 * body ของ `POST /v1/deliveries` — **หนึ่งฟิลด์**: เลขกล่องที่สแกนที่ประตู
 * (`DeliveryCreateRequest.package_no`) ไม่ใช่ `packing_unit_id` เพราะสิ่งที่
 * เครื่องสแกนอ่านคือเลขที่พิมพ์ไว้บนกล่อง ไม่มีคีย์ที่เซิร์ฟเวอร์เป็นเจ้าของเลย
 * (เลข delivery, จำนวนที่ส่งจริง, ผู้กระทำ — ดู `DELIVERY_FIELD_IS_SERVER_OWNED`)
 */
export interface CreateDeliveryBody {
  readonly package_no: string;
}

/**
 * body ของ `POST /v1/deliveries/{id}/reverse` — **หนึ่งฟิลด์ และต้องมี**
 * (`DeliveryReversalRequest.reason`) — เซิร์ฟเวอร์บังคับด้วย
 * `ck_delivery_reversal_record_is_whole` เช่นกัน ห้ามส่งสตริงว่างเพื่อให้ผ่านฟิลด์นี้
 */
export interface ReverseDeliveryBody {
  readonly reason: string;
}
